import { useState } from "react";
import PropTypes from "prop-types";
import { useDCContext } from "@src/context";
import { AppUtils, useIsMobileWidth } from "@src/utils";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";
import { Checkbox, Divider, Button, FormInput } from "@src/components/atoms";
import { notifSchema } from "./formHelpers";
import Form from "./Form";

const NotificationSettingsForm = ({
  title,
  industry,
  onSubmit,
  defaultValues,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { getDynamicReplacement } = useDCContext();

  const isMobile = useIsMobileWidth();

  const handleSubmit = async (data) => {
    setError("");
    setIsSubmitting(true);
    try {
      onSubmit && (await onSubmit(data));
    } catch (error) {
      setError("Failed to save changes. Please try again later.");
      setIsSubmitting(false);
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const shouldDisableOptIn2And3 = industry === "shipping";

  return (
    <div className=" mx-auto p-6">
      {title && <h3 className="mb-6 text-center">{title}</h3>}
      <Form
        onSubmit={(data) => {
          handleSubmit(data);
        }}
        schema={notifSchema}
        options={{
          defaultValues: {
            opt_in_accepted_level_1:
              defaultValues?.opt_in_accepted_level_1 ?? true,
            opt_in_accepted_level_2:
              defaultValues?.opt_in_accepted_level_2 ?? true,
            opt_in_accepted_level_3:
              defaultValues?.opt_in_accepted_level_3 ?? true,
            opt_in_accepted_level_4:
              defaultValues?.opt_in_accepted_level_4 ?? true,
          },
        }}
        className="space-y-6"
      >
        {(methods) => {
          return (
            <div className="py-6">
              <div
                className={`${isMobile ? "flex flex-col justify-center items-start py-6" : "grid grid-cols-2"} gap-6 mb-4`}
              >
                <div>
                  <p className="text-lg font-bold">
                    {getDynamicReplacement(
                      "captain__notifications__preferences__emails__title",
                      {},
                      "Email Notifications",
                    )}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {getDynamicReplacement(
                      "captain__notifications__preferences__emails__subtitle",
                      {},
                      "Professionals who receive Signol emails save significantly more fuel.",
                    )}
                  </p>
                </div>
                <div>
                  <Checkbox
                    type={"other"}
                    fontSize={"sm"}
                    id="opt_in_accepted_level_1"
                    name="opt_in_accepted_level_1"
                    disabled
                    label={
                      <div>
                        {getDynamicReplacement(
                          "captain__notifications__preferences__level_1",
                          {},
                          "Security and Operational",
                        )}
                      </div>
                    }
                    checked={methods.watch("opt_in_accepted_level_1")}
                    onChange={(e) => {
                      console.log(
                        "checking/unchecking opt_in_accepted_level_1",
                        e.target.value,
                      );
                      methods.setValue(
                        "opt_in_accepted_level_1",
                        e.target.checked,
                      );
                    }}
                    error={
                      methods.formState.errors.opt_in_accepted_level_1?.message
                    }
                  />
                  <Checkbox
                    type={"other"}
                    fontSize={"sm"}
                    id="opt_in_accepted_level_2"
                    name="opt_in_accepted_level_2"
                    label={
                      <div>
                        {getDynamicReplacement(
                          "captain__notifications__preferences__level_2",
                          {},
                          "Performance Reports",
                        )}
                      </div>
                    }
                    disabled={shouldDisableOptIn2And3}
                    checked={methods.watch("opt_in_accepted_level_2")}
                    onChange={(e) => {
                      console.log(
                        "checking/unchecking opt_in_accepted_level_2",
                        e.target.value,
                      );
                      methods.setValue(
                        "opt_in_accepted_level_2",
                        e.target.checked,
                      );
                    }}
                    error={
                      methods.formState.errors.opt_in_accepted_level_2?.message
                    }
                  />
                  <Checkbox
                    type={"other"}
                    fontSize={"sm"}
                    id="opt_in_accepted_level_3"
                    name="opt_in_accepted_level_3"
                    label={
                      <div>
                        {getDynamicReplacement(
                          "captain__notifications__preferences__level_3",
                          {},
                          "Special Updates",
                        )}
                      </div>
                    }
                    disabled={shouldDisableOptIn2And3}
                    checked={methods.watch("opt_in_accepted_level_3")}
                    onChange={(e) => {
                      console.log(
                        "checking/unchecking opt_in_accepted_level_3",
                        e.target.value,
                      );
                      methods.setValue(
                        "opt_in_accepted_level_3",
                        e.target.checked,
                      );
                    }}
                    error={
                      methods.formState.errors.opt_in_accepted_level_3?.message
                    }
                  />
                </div>
              </div>

              <Divider />

              <div
                className={`${isMobile ? "flex flex-col justify-center items-start py-6" : "grid grid-cols-2"} gap-6 mb-4`}
              >
                <div>
                  <p className="text-lg font-bold">
                    {getDynamicReplacement(
                      "captain__notifications__preferences__push__title",
                      {},
                      "Push Notifications",
                    )}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {getDynamicReplacement(
                      "captain__notifications__preferences__push__subtitle",
                      {},
                      "Signol occasionally sends relevant insights about your progress.",
                    )}
                  </p>
                </div>
                <div>
                  <Checkbox
                    type={"other"}
                    fontSize={"sm"}
                    id="opt_in_accepted_level_4"
                    name="opt_in_accepted_level_4"
                    label={
                      <div>
                        {getDynamicReplacement(
                          "captain__notifications__preferences__level_4",
                          {},
                          "Signol Insights",
                        )}
                      </div>
                    }
                    checked={methods.watch("opt_in_accepted_level_4")}
                    onChange={(e) => {
                      console.log(
                        "checking/unchecking opt_in_accepted_level_4",
                        e.target.value,
                      );
                      methods.setValue(
                        "opt_in_accepted_level_4",
                        e.target.checked,
                      );
                    }}
                    error={
                      methods.formState.errors.opt_in_accepted_level_4?.message
                    }
                  />
                </div>
              </div>
              <div className="flex justify-center">
                <div className={`${!!isMobile ? "w-full" : ""}`}>
                  <Button submit loading={isSubmitting}>
                    {getDynamicReplacement(
                      "captain__notifications__preferences__save",
                      {},
                      "Save changes",
                    )}
                  </Button>
                </div>
              </div>
            </div>
          );
        }}
      </Form>
      {error && <div>{error}</div>}
    </div>
  );
};

export const notificationSettingsFormControls = {
  title: { propType: PropTypes.string },
  industry: COMMON_CONTROLS.industry,
  onSubmit: {
    propType: PropTypes.func.isRequired,
    description: "Form submission handler from parent",
    disable: true,
  },
};

NotificationSettingsForm.propTypes = AppUtils.getPropTypeFromControls(
  notificationSettingsFormControls,
);

export default NotificationSettingsForm;
